import Image from "next/image";
import { motion } from 'framer-motion';
import { fadeInOut } from '@utils/motion/fade-in-out';
import { IoIosCloseCircle } from 'react-icons/io';
import Counter from '@components/common/counter';
import { useRemoveFromCartMutation, useUpdateCartQuantityMutation } from '@framework/cart/all-cart';
import { useTranslation } from 'next-i18next';
import { CartItems } from '@framework/types';
import { ROUTES } from '@utils/routes';
import { useRouter } from 'next/router';
import { getPublicBaseUrl } from "@utils/getPublicBaseUrl";
import { getCurrentLocationAndLanguage } from "@utils/location";
type CartItemProps = {
  item: CartItems;
  cartId: number;
};
const CartItem: React.FC<CartItemProps> = ({
  item,
  cartId
}) => {
  const {
    t
  } = useTranslation('common');
  const router = useRouter();
  const {
    mutate: removeItemFromCart
  } = useRemoveFromCartMutation();
  const {
    mutate: updateCartQuantity
  } = useUpdateCartQuantityMutation();
  const price = item?.price;
  const cc = item?.cc;
  const {
    location
  } = getCurrentLocationAndLanguage();
  const handleIncrement = () => {
    const newQuantity = item.quantity + 1;
    updateCartQuantity({
      cartId,
      productCode: item.productCode,
      quantity: newQuantity
    });
  };
  const handleDecrement = () => {
    if (item.quantity > 1) {
      const newQuantity = item.quantity - 1;
      updateCartQuantity({
        cartId,
        productCode: item.productCode,
        quantity: newQuantity
      });
    } else {
      removeItemFromCart({
        productCode: item.productCode,
        cartId: null
      });
    }
  };
  function navigateToProductPage() {
    const productIdentifier = item.id;
    if (!productIdentifier || !item.name) {
      return;
    }
    const productSlug = `${productIdentifier}-${item.name.toLowerCase().replace(/\s+/g, '-')}`;
    router.push(`${ROUTES.SHOP}/${productSlug}`, undefined, {
      locale: router.locale
    });
  }
  return <motion.div layout initial="from" animate="to" exit="from" variants={fadeInOut(0.25)} className="group w-full h-auto flex justify-start items-center bg-white dark:bg-zinc-950 py-4 md:py-7 border-b border-neutral-100 dark:border-neutral-800 relative last:border-b-0" title={item?.name} data-sentry-element="unknown" data-sentry-component="CartItem" data-sentry-source-file="cart-item.tsx">
      <div className="relative flex flex-shrink-0 w-24 h-24 overflow-hidden bg-neutral-200 dark:bg-neutral-800 rounded-md cursor-pointer md:w-28 md:h-28 ltr:mr-4 rtl:ml-4">
        <Image src={item?.img ? `${getPublicBaseUrl()}${item.img}` : item?.photo?.x600 ? `${getPublicBaseUrl()}${item.photo.x600}` : '/assets/placeholder/cart-item.svg'} width={600} height={600} loading="eager" alt={item.name || 'Product Image'} className="object-cover bg-neutral-300 dark:bg-neutral-800 p-1" data-sentry-element="Image" data-sentry-source-file="cart-item.tsx" />
        <div className="absolute top-0 flex items-center justify-center w-full h-full transition duration-200 ease-in-out bg-black ltr:left-0 rtl:right-0 bg-opacity-30 md:bg-opacity-0 md:group-hover:bg-opacity-30" onClick={() => removeItemFromCart({
        productCode: item.productCode,
        cartId: null
      })} role="button">
          <IoIosCloseCircle className="relative text-2xl text-white dark:text-neutral-600 transition duration-300 ease-in-out transform md:scale-0 md:opacity-0 md:group-hover:scale-100 md:group-hover:opacity-100" data-sentry-element="IoIosCloseCircle" data-sentry-source-file="cart-item.tsx" />
        </div>
      </div>
      <div className="flex flex-col w-full overflow-hidden">
      <div onClick={navigateToProductPage} className="truncate text-sm text-heading dark:text-neutral-200 mb-1.5 -mt-1 cursor-pointer" role="button">
       <span className="text-xs">#{item.productCode}</span>   {item.name}
        </div>
        <span className="text-xs text-neutral-400 dark:text-neutral-600 mb-1.5">
          {t('text-unit-price')} : {price}
        </span>
        <span className="text-xs text-neutral-400 dark:text-neutral-600 mb-1.5">
          {t('text-cc')} : {cc}
        </span>
        {item.quantity >= 30 && <p className="text-xs text-orange-500 mb-1">
            {t('common:text-max-quantity-reached')}
          </p>}
        <div className="flex items-end justify-between">
          <Counter quantity={item.quantity} onIncrement={handleIncrement} onDecrement={handleDecrement} variant="dark" disableIncrement={item.quantity >= 30} data-sentry-element="Counter" data-sentry-source-file="cart-item.tsx" />
          <span className="text-sm font-semibold leading-5 md:text-base text-heading dark:text-neutral-200">
            {location === 'tr' ? item.retailCost : item.cost}
          </span>
        </div>
      </div>
    </motion.div>;
};
export default CartItem;